import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    Input,
    message,
    Row,
    Col,
    Divider,
    Upload,
    Popconfirm,
    Space,
} from "antd";
import dayjs from "dayjs";
import api from "../../api";
import {LeftOutlined} from "@ant-design/icons";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;

const FlightDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [flight, setFlight] = useState<any>(null);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const inDepartureDate = Form.useWatch("in_departure_date", form);
    const inArrivalDate = Form.useWatch("in_arrival_date", form);
    const [manuallyEditedInArrival, setManuallyEditedInArrival] = useState(false);
    const [manuallyEditedOutArrival, setManuallyEditedOutArrival] = useState(false);
    const outDepartureDate = Form.useWatch("out_departure_date", form);
    const outArrivalDate = Form.useWatch("out_arrival_date", form);
    const selectedCurrency = Form.useWatch("flight_cost_currency", form);
    const [newDocuments, setNewDocuments] = useState<any[]>([]);
    const [adjustments, setAdjustments] = useState<any[]>([]);
    const [documentError, setDocumentError] = useState<string | null>(null);
    const [documentErrors, setDocumentErrors] = useState<{ label?: boolean; file?: boolean }[]>([]);
    const [adjustmentError, setAdjustmentError] = useState<string | null>(null);
    const [adjustmentErrors, setAdjustmentErrors] = useState<{ label?: boolean; amount?: boolean }[]>([]);

    useEffect(() => {
        fetchFlight();
    }, [id]);

    useEffect(() => {
        if (inDepartureDate && !manuallyEditedInArrival) {
            form.setFieldsValue({in_arrival_date: inDepartureDate});
        }
    }, [inDepartureDate]);

    useEffect(() => {
        if (
            inArrivalDate &&
            inDepartureDate &&
            !inArrivalDate.isSame(inDepartureDate, "day")
        ) {
            setManuallyEditedInArrival(true);
        }
    }, [inArrivalDate]);

    useEffect(() => {
        if (outDepartureDate && !manuallyEditedOutArrival) {
            form.setFieldsValue({out_arrival_date: outDepartureDate});
        }
    }, [outDepartureDate]);

    useEffect(() => {
        if (
            outArrivalDate &&
            outDepartureDate &&
            !outArrivalDate.isSame(outDepartureDate, "day")
        ) {
            setManuallyEditedOutArrival(true);
        }
    }, [outArrivalDate]);

    useEffect(() => {
        form.setFieldsValue({
            flight_cost_currency: "KRW"
        });
    }, [form]);

    const fetchFlight = async () => {
        try {
            const res = await api.get(`/cc/flights/${id}/`);
            setFlight(res.data);
            form.setFieldsValue({
                ...res.data,
                in_departure_date: res.data.in_departure_date ? dayjs(res.data.in_departure_date) : null,
                in_arrival_date: res.data.in_arrival_date ? dayjs(res.data.in_arrival_date) : null,
                out_departure_date: res.data.out_departure_date ? dayjs(res.data.out_departure_date) : null,
                out_arrival_date: res.data.out_arrival_date ? dayjs(res.data.out_arrival_date) : null,
                in_departure_time: res.data.in_departure_time ? dayjs(res.data.in_departure_time, "HH:mm") : null,
                in_arrival_time: res.data.in_arrival_time ? dayjs(res.data.in_arrival_time, "HH:mm") : null,
                out_departure_time: res.data.out_departure_time ? dayjs(res.data.out_departure_time, "HH:mm") : null,
                out_arrival_time: res.data.out_arrival_time ? dayjs(res.data.out_arrival_time, "HH:mm") : null
            });

        } catch {
            message.error("Failed to fetch flight.");
        }
    };

    const handleUpdate = async (values: any) => {
        try {
            // 🛑 Validate new documents
            const newDocErrors = newDocuments.map((doc) => ({
                label: !doc.label,
                file: !doc.file,
            }));

            const hasDocErrors = newDocErrors.some(err => err.label || err.file);

            if (hasDocErrors) {
                setDocumentErrors(newDocErrors);
                return;
            } else {
                setDocumentErrors([]);
            }

            // 🛑 Validate adjustments
            const newErrors = adjustments.map((adj) => ({
                label: !adj.label,
                amount: !adj.amount && adj.amount !== 0, // disallow null/empty but allow 0
            }));

            const hasAnyErrors = newErrors.some(err => err.label || err.amount);

            if (hasAnyErrors) {
                setAdjustmentErrors(newErrors);
                setAdjustmentError("All cost adjustments must include a label and an amount.");
                return;
            } else {
                setAdjustmentErrors([]);
                setAdjustmentError(null);
            }

            // ✅ 1. Update the base flight info
            const payload = {
                ...values,
                customer_id: flight.customer.id,
                in_departure_date: values.in_departure_date?.format("YYYY-MM-DD") || null,
                in_arrival_date: values.in_arrival_date?.format("YYYY-MM-DD") || null,
                out_departure_date: values.out_departure_date?.format("YYYY-MM-DD") || null,
                out_arrival_date: values.out_arrival_date?.format("YYYY-MM-DD") || null,
                in_departure_time: values.in_departure_time?.format("HH:mm:ss") || null,
                in_arrival_time: values.in_arrival_time?.format("HH:mm:ss") || null,
                out_departure_time: values.out_departure_time?.format("HH:mm:ss") || null,
                out_arrival_time: values.out_arrival_time?.format("HH:mm:ss") || null,
                flight_cost: values.flight_cost
                    ? parseInt(values.flight_cost.toString().replace(/,/g, ""))
                    : null,
            };

            await api.put(`/cc/flights/${flight.id}/`, payload);

            // ✅ 2. Upload new documents
            for (const doc of newDocuments) {
                const formData = new FormData();
                formData.append("flight", flight.id);
                formData.append("label", doc.label);
                formData.append("file", doc.file);

                await api.post("/cc/flight-documents/", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            }

            // Enforce refunds to be negative
            const normalizedAdjustments = adjustments.map((adj) => {
                let amount = parseFloat(adj.amount);

                if (adj.label === "Refund" && amount > 0) {
                    amount = -amount;
                }

                return {
                    ...adj,
                    amount,
                };
            });

            //  Post new cost adjustments
            for (const adj of normalizedAdjustments) {
                await api.post("/cc/flight-adjustments/", {
                    flight: flight.id,
                    label: adj.label,
                    amount: parseFloat(adj.amount),
                    notes: adj.notes || "",
                    currency: "KRW",
                });
            }

            // ✅ Success
            setNewDocuments([]);
            setAdjustments([]);
            setIsModalVisible(false);
            fetchFlight();

        } catch (err) {
            // Fallback error (network/server/unknown)
            console.error(err);
            setDocumentError("Something went wrong while updating the flight.");
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: "Are you sure you want to delete this flight reservation?",
            content: "This action cannot be undone.",
            okText: "Yes, delete it",
            okType: "danger",
            cancelText: "Cancel",
            centered: true,
            onOk: async () => {
                try {
                    await api.delete(`/cc/flights/${id}/`);
                    message.success("Flight reservation deleted");
                    navigate("/cc/flights");
                } catch {
                    message.error("Failed to delete flight reservation");
                }
            },
        });
    };

    if (!flight) return null;

    return (
        <div style={{padding: 24}}>
            <Button
                icon={<LeftOutlined/>}
                onClick={() => navigate(-1)}
                style={{marginBottom: 24}}
                className="rounded-md"
            >
                Back
            </Button>

            <Title level={3} style={{marginBottom: 16}}>
                Flight Details
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Casino Club Flight Overview
                            </Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button danger onClick={handleDelete} className="rounded-md">
                                    Delete
                                </Button>
                                <Button type="primary" onClick={() => setIsModalVisible(true)} className="rounded-md">
                                    Edit
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                }
            >
                <Descriptions
                    bordered
                    column={1}
                    style={maxW700}
                >
                    <Descriptions.Item label="Customer" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.customer?.first_name} {flight.customer?.last_name}
                        </NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status"
                                       labelStyle={boldLabelStyle}><NoWrap>{flight.status}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Reservation Number"
                                       labelStyle={boldLabelStyle}><NoWrap>{flight.reservation_number}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Memo"
                                       labelStyle={boldLabelStyle}><NoWrap>{flight.memo}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Cabin"
                                       labelStyle={boldLabelStyle}><NoWrap>{flight.cabin}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="IN Departure Date"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.in_departure_date}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="IN Flight Number" labelStyle={boldLabelStyle}><NoWrap>{flight.in_flight_number}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="IN Departure Airport"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.in_departure_airport}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="IN Departure Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_departure_time ? dayjs(flight.in_departure_time, "HH:mm:ss").format("HH:mm") : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="IN Arrival Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.in_arrival_time ? dayjs(flight.in_arrival_time, "HH:mm:ss").format("HH:mm") : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item
                        label="OUT Departure Date"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.out_departure_date}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="OUT Flight Number"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.out_flight_number}</NoWrap></Descriptions.Item>
                    <Descriptions.Item
                        label="OUT Departure Airport"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.out_departure_airport}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="OUT Departure Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_departure_time ? dayjs(flight.out_departure_time, "HH:mm:ss").format("HH:mm") : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item
                        label="OUT Arrival Airport"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.out_arrival_airport}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="OUT Arrival Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.out_arrival_time ? dayjs(flight.out_arrival_time, "HH:mm:ss").format("HH:mm") : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item
                        label="Flight Cost Currency"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.flight_cost_currency || "-"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Flight Cost" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.flight_cost != null
                            ? flight.flight_cost.toLocaleString()
                            : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item
                        label="Exchange Rate"
                        labelStyle={boldLabelStyle}><NoWrap>{flight.flight_cost_exchange_rate || "-"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Total Flight Cost (KRW)" labelStyle={boldLabelStyle}>
                        <NoWrap>{flight.total_flight_cost_krw != null
                            ? Math.round(flight.total_flight_cost_krw).toLocaleString() + " KRW"
                            : "-"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Documents" labelStyle={boldLabelStyle}>
                        <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                            {flight.documents?.length === 0 && (
                                <NoWrap>No documents uploaded.</NoWrap>
                            )}

                            {flight.documents?.map((doc: any) => (
                                <div key={doc.id} style={{display: "flex", flexDirection: "column", gap: 4}}>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 8
                                    }}>
                                        <div style={{flex: 1}}>
                                            <div style={{wordBreak: "break-all"}}>
                                                <strong>{doc.label}:</strong>{" "}
                                                <a
                                                    href={doc.file}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        wordBreak: "break-all",
                                                        display: "inline-block",
                                                        maxWidth: "100%"
                                                    }}
                                                >
                                                    {doc.file?.split("/").pop() || "View File"}
                                                </a>
                                            </div>
                                        </div>

                                        <div style={{display: "flex", gap: 8}}>
                                            <Upload
                                                beforeUpload={async (file) => {
                                                    const isUnder10MB = file.size / 1024 / 1024 < 10;
                                                    if (!isUnder10MB) {
                                                        message.error(`${file.name} is too large. Max size is 10MB.`);
                                                        return Upload.LIST_IGNORE;
                                                    }

                                                    const formData = new FormData();
                                                    formData.append("file", file);

                                                    try {
                                                        await api.patch(`/cc/flight-documents/${doc.id}/`, formData, {
                                                            headers: {"Content-Type": "multipart/form-data"},
                                                        });
                                                        message.success("File replaced");
                                                        fetchFlight();
                                                    } catch {
                                                        message.error("Failed to replace file");
                                                    }

                                                    return false; // Prevent default upload
                                                }}
                                                customRequest={() => {
                                                }}
                                                showUploadList={false}
                                            >
                                                <Button size="small">Replace</Button>
                                            </Upload>

                                            <Popconfirm
                                                title="Delete this document?"
                                                onConfirm={async () => {
                                                    try {
                                                        await api.delete(`/cc/flight-documents/${doc.id}/`);
                                                        message.success("Document deleted");
                                                        fetchFlight();
                                                    } catch {
                                                        message.error("Failed to delete document");
                                                    }
                                                }}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button danger size="small">Delete</Button>
                                            </Popconfirm>
                                        </div>
                                    </div>

                                    <div style={{fontSize: 12, color: "#888", marginLeft: 4}}>
                                        Uploaded by <strong>{doc.uploaded_by_name || "Unknown"}</strong> on{" "}
                                        {dayjs(doc.uploaded_at).format("YYYY-MM-DD HH:mm")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Descriptions.Item>

                    <Descriptions.Item label="Cost Adjustments (Total)" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {flight.adjustments?.length
                                ? (
                                    Math.round(
                                        flight.adjustments.reduce((acc: number, a: any) => acc + Number(a.amount_krw || 0), 0)
                                    ).toLocaleString() + " KRW"
                                )
                                : "-"
                            }
                        </NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Final Cost (KRW)" labelStyle={boldLabelStyle}>
                        <NoWrap>
                            {(
                                Math.round(Number(flight.total_flight_cost_krw || 0) +
                                    (flight.adjustments?.reduce((acc: number, a: any) => acc + Number(a.amount_krw || 0), 0) || 0))
                            ).toLocaleString()} KRW
                        </NoWrap>
                    </Descriptions.Item>
                </Descriptions>

                <Divider orientation="left">Cost Adjustments</Divider>

                {flight.adjustments?.length === 0 && <NoWrap>No cost adjustments recorded.</NoWrap>}

                {flight.adjustments?.map((adj: any) => (
                    <div key={adj.id} style={{marginBottom: 16, maxWidth: 500}}>
                        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                            <div>
                                <strong>{adj.label}</strong> :{" "}
                                {Number(adj.amount).toLocaleString()} {adj.currency}

                                {adj.currency !== "KRW" && (
                                    <>
                                        {" "}→{" "}
                                        <strong>{Math.round(Number(adj.amount_krw)).toLocaleString()} KRW</strong>
                                    </>
                                )}

                                {adj.notes && (
                                    <div style={{color: "#888", fontSize: 13}}>
                                        Note: {adj.notes}
                                    </div>
                                )}
                            </div>

                            <Popconfirm
                                title="Delete this cost adjustment?"
                                onConfirm={async () => {
                                    try {
                                        await api.delete(`/cc/flight-adjustments/${adj.id}/`);
                                        message.success("Adjustment deleted");
                                        fetchFlight();
                                    } catch {
                                        message.error("Failed to delete adjustment");
                                    }
                                }}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button size="small" danger>Delete</Button>
                            </Popconfirm>
                        </div>

                        <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                            Added by <strong>{adj.created_by_name || "Unknown"}</strong> on{" "}
                            {dayjs(adj.created_at).format("YYYY-MM-DD HH:mm")}
                        </div>
                    </div>
                ))}

                <Modal
                    title="Edit Flight"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onOk={() => form.submit()}
                    centered
                    width={800}
                    okButtonProps={{className: "rounded-md"}}
                    cancelButtonProps={{className: "rounded-md"}}
                    bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
                >
                    <Form layout="vertical" form={form} onFinish={handleUpdate}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="status" label="Status" rules={[{required: true}]}>
                                    <Select>
                                        <Option value="Not Started">Not Started</Option>
                                        <Option value="Flight Options Sent">Flight Options Sent</Option>
                                        <Option value="Waiting for Ticketing">Waiting for Ticketing</Option>
                                        <Option value="E-Ticket Sent">E-Ticket Sent</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="reservation_number" label="Reservation Number">
                                    <Input placeholder="Enter reservation number"/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="cabin" label="Cabin">
                                    <Select>
                                        <Option value="Economy">Economy</Option>
                                        <Option value="Prestige">Prestige</Option>
                                        <Option value="Business">Business</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">IN (→ Korea)</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="in_departure_date" label="Departure Date">
                                    <DatePicker style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="in_arrival_date" label="Arrival Date">
                                    <DatePicker
                                        style={{width: "100%"}}
                                        disabledDate={(current) =>
                                            inDepartureDate ? current && current.isBefore(inDepartureDate, "day") : false
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="in_flight_number" label="Flight Number"><Input/></Form.Item>
                        <Form.Item name="in_departure_airport" label="Departure Airport"><Input/></Form.Item>
                        <Form.Item name="in_departure_time" label="Departure Time"><TimePicker format="HH:mm"
                                                                                               style={{width: "100%"}}/></Form.Item>
                        <Form.Item name="in_arrival_airport" label="Arrival Airport">
                            <Select>
                                <Option value="ICN-T1">ICN-T1</Option>
                                <Option value="ICN-T2">ICN-T2</Option>
                                <Option value="GMP">GMP</Option>
                                <Option value="GMP-Private">GMP-Private</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="in_arrival_time" label="Arrival Time"><TimePicker format="HH:mm"
                                                                                           style={{width: "100%"}}/></Form.Item>

                        <Divider orientation="left">OUT (Korea →)</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="out_departure_date" label="Departure Date">
                                    <DatePicker
                                        style={{width: "100%"}}
                                        disabledDate={(current) =>
                                            inArrivalDate ? current && current.isBefore(inArrivalDate, "day") : false
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="out_arrival_date" label="Arrival Date">
                                    <DatePicker
                                        style={{width: "100%"}}
                                        disabledDate={(current) =>
                                            outDepartureDate ? current && current.isBefore(outDepartureDate, "day") : false
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="out_flight_number" label="Flight Number"><Input/></Form.Item>
                        <Form.Item name="out_departure_airport" label="Departure Airport">
                            <Select>
                                <Option value="ICN-T1">ICN-T1</Option>
                                <Option value="ICN-T2">ICN-T2</Option>
                                <Option value="GMP">GMP</Option>
                                <Option value="GMP-Private">GMP-Private</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="out_departure_time" label="Departure Time"><TimePicker format="HH:mm"
                                                                                                style={{width: "100%"}}/></Form.Item>
                        <Form.Item name="out_arrival_airport" label="Arrival Airport"><Input/></Form.Item>
                        <Form.Item name="out_arrival_time" label="Arrival Time"><TimePicker format="HH:mm"
                                                                                            style={{width: "100%"}}/></Form.Item>
                        <Col span={12}>
                            <Form.Item name="memo" label="Memo">
                                <Input.TextArea placeholder="Enter memo" rows={3}/>
                            </Form.Item>
                        </Col>

                        <Divider orientation="left">Cost</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="flight_cost_currency" label="Currency">
                                    <Select placeholder="Select currency">
                                        <Option value="Yen">Yen</Option>
                                        <Option value="KRW">KRW</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="flight_cost"
                                    label="Flight Cost"
                                    getValueFromEvent={(e) => parseInt(e.target.value.replace(/,/g, ""))}
                                >
                                    <Input
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/,/g, "");
                                            const numeric = rawValue.replace(/\D/g, "");
                                            form.setFieldsValue({flight_cost: Number(numeric).toLocaleString()});
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {selectedCurrency === "Yen" && (
                            <Form.Item
                                name="flight_cost_exchange_rate"
                                label="Exchange Rate (Yen ➝ KRW)"
                                rules={[{required: true, message: "Exchange rate required for Yen"}]}
                            >
                                <Input type="number" step="0.01"/>
                            </Form.Item>
                        )}

                        <Divider orientation="left">New Documents</Divider>

                        <Row>
                            <Col span={24}>
                                {newDocuments.map((doc, index) => (
                                    <Row gutter={8} key={index} className="mb-2" align="middle">
                                        <Col span={24}>
                                            <div style={{
                                                display: "flex",
                                                gap: 8,
                                                alignItems: "center",
                                                flexWrap: "wrap"
                                            }}>
                                                <div style={{display: "flex", flexDirection: "column"}}>
                                                    <Select
                                                        placeholder="Label"
                                                        value={doc.label || null}
                                                        onChange={(value) => {
                                                            const updated = [...newDocuments];
                                                            updated[index].label = value;
                                                            setNewDocuments(updated);
                                                        }}
                                                        style={{width: 200}}
                                                        status={documentErrors?.[index]?.label ? "error" : undefined}
                                                    >
                                                        <Option value="Invoice">Invoice</Option>
                                                        <Option value="Itinerary">Itinerary</Option>
                                                        <Option value="Change Fee">Change Fee</Option>
                                                        <Option value="Cabin Upgrade">Cabin Upgrade</Option>
                                                        <Option value="Refund">Refund</Option>
                                                        <Option value="Other">Other</Option>
                                                    </Select>
                                                    {documentErrors?.[index]?.label && (
                                                        <div style={{color: "red", fontSize: 12}}>Label required</div>
                                                    )}
                                                </div>

                                                <div style={{display: "flex", flexDirection: "column"}}>
                                                    <Upload
                                                        beforeUpload={(file) => {
                                                            const isUnder10MB = file.size / 1024 / 1024 < 10;
                                                            if (!isUnder10MB) {
                                                                setDocumentErrors(prev => {
                                                                    const updated = [...prev];
                                                                    updated[index] = {
                                                                        ...(updated[index] || {}),
                                                                        file: true
                                                                    };
                                                                    return updated;
                                                                });
                                                                return Upload.LIST_IGNORE;
                                                            }

                                                            const updated = [...newDocuments];
                                                            updated[index].file = file;
                                                            setNewDocuments(updated);

                                                            setDocumentErrors(prev => {
                                                                const updated = [...prev];
                                                                updated[index] = {
                                                                    ...(updated[index] || {}),
                                                                    file: false
                                                                };
                                                                return updated;
                                                            });

                                                            return false;
                                                        }}
                                                        customRequest={() => {
                                                        }}
                                                        showUploadList={false}
                                                    >
                                                        <Button>Select File</Button>
                                                    </Upload>

                                                    {doc.file && (
                                                        <div style={{fontSize: 12, marginTop: 4, color: "#555"}}>
                                                            {doc.file.name}
                                                        </div>
                                                    )}
                                                    {documentErrors?.[index]?.file && (
                                                        <div style={{color: "red", fontSize: 12}}>File required</div>
                                                    )}
                                                </div>

                                                <Button
                                                    danger
                                                    type="link"
                                                    onClick={() => {
                                                        const updated = [...newDocuments];
                                                        updated.splice(index, 1);

                                                        const updatedErrors = [...documentErrors];
                                                        updatedErrors.splice(index, 1);

                                                        setNewDocuments(updated);
                                                        setDocumentErrors(updatedErrors);
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                ))}

                                <Button
                                    type="dashed"
                                    onClick={() =>
                                        setNewDocuments([...newDocuments, {label: null, file: null}])
                                    }
                                    block
                                >
                                    + Add Document
                                </Button>
                            </Col>
                        </Row>


                        <Divider orientation="left">Add Adjustments</Divider>

                        {adjustments.map((adj, index) => (
                            <Row gutter={8} key={index} className="mb-2">
                                <Col span={8}>
                                    <div style={{marginBottom: 4}}>
                                        <Select
                                            placeholder="Label"
                                            value={adj.label || null}
                                            onChange={(value) => {
                                                const updated = [...adjustments];
                                                updated[index].label = value;
                                                setAdjustments(updated);
                                            }}
                                            style={{width: "100%"}}
                                            status={adjustmentErrors?.[index]?.label ? "error" : undefined}
                                        >
                                            <Option value="Change Fee">Change Fee</Option>
                                            <Option value="Cabin Upgrade">Cabin Upgrade</Option>
                                            <Option value="Refund">Refund</Option>
                                            <Option value="Other">Other</Option>
                                        </Select>
                                        {adjustmentErrors?.[index]?.label && (
                                            <div style={{color: "red", fontSize: 12}}>Label required</div>
                                        )}
                                    </div>
                                </Col>

                                <Col span={8}>
                                    <div style={{marginBottom: 4}}>
                                        <Input
                                            placeholder="Amount (KRW)"
                                            type="text"
                                            value={adj.amount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || ""}
                                            onChange={(e) => {
                                                let rawValue = e.target.value.replace(/,/g, ""); // remove commas
                                                const parsed = parseFloat(rawValue);

                                                const updated = [...adjustments];
                                                const isRefund = updated[index].label === "Refund";

                                                if (!isNaN(parsed)) {
                                                    // Enforce refund to be negative
                                                    if (isRefund && parsed > 0) {
                                                        updated[index].amount = -parsed;
                                                    } else {
                                                        updated[index].amount = parsed;
                                                    }
                                                } else {
                                                    updated[index].amount = "";
                                                }

                                                setAdjustments(updated);
                                            }}
                                            status={adjustmentErrors?.[index]?.amount ? "error" : undefined}
                                        />
                                        {adjustmentErrors?.[index]?.amount && (
                                            <div style={{color: "red", fontSize: 12}}>Amount required</div>
                                        )}
                                    </div>
                                </Col>

                                <Col span={2}>
                                    <Button
                                        danger
                                        type="link"
                                        onClick={() => {
                                            const updated = [...adjustments];
                                            updated.splice(index, 1);

                                            const updatedErrors = [...adjustmentErrors];
                                            updatedErrors.splice(index, 1);
                                            setAdjustments(updated);
                                            setAdjustmentErrors(updatedErrors);
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </Col>
                            </Row>
                        ))}

                        <Button
                            type="dashed"
                            onClick={() => setAdjustments([...adjustments, {label: null, amount: '', notes: ''}])}
                            block
                        >
                            + Add Adjustment
                        </Button>
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default FlightDetailPage;
